import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["All", "Expedition Planning", "Satellite Imagery", "Marine Observation", "Partnerships", "Safety & Conservation"];

const KIND_STYLES = {
  "CONCEPT NOTE": "text-oreef border-oreef/35",
  "EXPLAINER": "text-otropical border-otropical/35",
  "FIELD NOTE": "text-ogold border-ogold/35",
  "TARGET": "text-ocoral border-ocoral/35",
};

export default function OceanUpdates() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    document.title = "Field Journal · Arjuna Indian Ocean Expedition";
    axios.get(`${API}/ocean/updates`).then((r) => setUpdates(r.data)).catch(() => setUpdates([])).finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => (filter === "All" ? updates : updates.filter((u) => u.category === filter)), [updates, filter]);

  return (
    <main className="min-h-screen bg-onavy" data-testid="ocean-updates-page">
      <header className="border-b border-oreef/10 bg-oabyss/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
          <Link to="/ocean" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-oreef hover:text-oseaglass transition-colors" data-testid="ocean-updates-back-link">
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Mission Home
          </Link>
          <span className="font-mono text-[10px] tracking-[0.3em] text-oslate hidden sm:block">ARJUNA INDIAN OCEAN · FIELD JOURNAL</span>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-ocoral" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-oreef">Editorial</span>
          </div>
          <h1 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-oseaglass leading-[0.95]" data-testid="ocean-editorial-header">
            Mission updates &amp; <span className="text-oreef">field journal</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-oseaglass/65 leading-relaxed">
            Expedition planning, coastal geography, dive preparation, marine-observation methods and satellite explainers
            — published as clearly labelled concept notes until genuine field observations exist.
          </p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2" role="group" aria-label="Filter journal by category">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              data-testid={`ocean-editorial-filter-${f.toLowerCase().replace(/[\s&]+/g, "-")}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                filter === f ? "border-ocoral bg-ocoral text-onavy" : "border-oreef/20 text-oslate hover:border-oreef/50 hover:text-oreef"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 font-mono text-xs tracking-[0.3em] text-oslate" data-testid="ocean-updates-loading">RECEIVING TRANSMISSION…</div>
        ) : visible.length === 0 ? (
          <div className="mt-16 border border-oreef/15 bg-oabyss/70 p-10 max-w-xl" data-testid="ocean-updates-empty">
            <p className="text-sm text-oseaglass/60 leading-relaxed">No entries in this category yet. Genuine mission progress will be published here as it happens.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {visible.map((u, i) => (
              <motion.article
                key={u.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                className="border border-oreef/15 bg-oabyss/80 p-7 flex flex-col hover:border-oreef/45 transition-colors duration-300"
                data-testid={`ocean-dispatch-card-${i + 1}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className={`font-mono text-[9px] tracking-[0.25em] px-2 py-1 border ${KIND_STYLES[u.kind] || KIND_STYLES["CONCEPT NOTE"]}`}>{u.kind}</span>
                  <span className="font-mono text-[10px] text-oslate tracking-widest">{u.date}</span>
                </div>
                <h2 className="font-display font-semibold text-2xl text-oseaglass mt-5 tracking-wide leading-tight">{u.title}</h2>
                <p className="text-sm text-oseaglass/60 mt-3 leading-relaxed flex-1">{u.summary}</p>
                <div className="mt-6 font-mono text-[10px] tracking-[0.25em] text-oslate uppercase">{u.category}</div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
